import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { BlockDef } from "./block.interfaces";
import { arrayField, createBlock, headingField, htmlField } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    GridWithRoundImages,
    GridWithRoundImagesProps,
    GridWithLargeRoundImages,
    GridWithLargeRoundImagesProps,
    FullWidthWithVerticalImages,
    FullWidthWithVerticalImagesProps,
    WithSmallImages,
    WithSmallImagesProps,
    WithImageAndShortParagraph,
    WithImageAndShortParagraphProps,
    WithLargeImages,
    WithLargeImagesProps,
    WithVerticalImages,
    WithVerticalImagesProps,
} from "@/views/blocks/team/team";
import { AVATAR_API_BASE_URL } from "../page.service";
import { useFieldConfig } from "./useFieldConfig";

export function useTeams() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const withSmallImagesBlock = computed<BlockDef>(() =>
        createBlock<WithSmallImagesProps>({
            typeId: "WithSmallImages",
            type: "team",
            name: t("page.blocks.team.withSmallImages"),
            component: WithSmallImages,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Our expert team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We're an innovative team of professionals committed to excellence in our field and focused on creating exceptional value for everyone we serve.",
                    translations.value.text
                )
            )
            .withArrayField(
                "members",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                    },
                    [
                        {
                            name: "John Doe",
                            role: "CEO",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                        },
                        {
                            name: "Jane Doe",
                            role: "CTO",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/1`,
                        },
                        {
                            name: "Garry Doe",
                            role: "CFO",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/2`,
                        },
                        {
                            name: "Goerge Doe",
                            role: "CMO",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                        },
                        {
                            name: "Elon Doe",
                            role: "Developer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/4`,
                        },
                        {
                            name: "Alex Doe",
                            role: "Designer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/5`,
                        },
                    ],
                    {
                        name: "New member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/6`,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const fullWidthWithVerticalImagesBlock = computed<BlockDef>(() =>
        createBlock<FullWidthWithVerticalImagesProps>({
            typeId: "FullWidthWithVerticalImages",
            type: "team",
            name: t("page.blocks.team.fullWidthWithVerticalImages"),
            component: FullWidthWithVerticalImages,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Our Leadership Team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our talented team brings years of expertise and passion, delivering innovative solutions that drive success for our clients.",
                    translations.value.text
                )
            )
            .withArrayField(
                "teamMembers",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                        description: { label: translations.value.description, type: "text" },
                    },
                    [
                        {
                            name: "Michael Chen",
                            role: "Chief Technology Officer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                            description:
                                "Leading our technical initiatives with over 15 years of experience in software architecture and emerging technologies. His expertise in scalable solutions helps our clients stay ahead of industry trends and achieve their business goals.",
                        },
                        {
                            name: "Sarah Johnson",
                            role: "Head of Design",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/2`,
                            description:
                                "Brings creative vision to every project with her background in user experience and brand identity development. Her combination of aesthetic sensibility with practical functionality creates designs that resonate with target audiences and drive engagement.",
                        },
                        {
                            name: "David Rodriguez",
                            role: "Product Director",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                            description:
                                "Transforms complex business requirements into elegant product solutions. With his expertise in agile methodologies, David ensures our development process remains efficient while delivering exceptional results.",
                        },
                        {
                            name: "Emily Chen",
                            role: "Chief Marketing Officer",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/4`,
                            description:
                                "Drives our marketing strategy with innovative approaches to digital and traditional channels. With over a decade of experience in brand development, Emily excels at creating compelling narratives that connect with audiences and deliver measurable business growth.",
                        },
                    ],
                    {
                        name: "New team member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/3`,
                        description: "Add a description for the new team member here.",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const gridWithLargeRoundImagesBlock = computed<BlockDef>(() =>
        createBlock<GridWithLargeRoundImagesProps>({
            typeId: "GridWithLargeRoundImages",
            type: "team",
            name: t("page.blocks.team.gridWithLargeRoundImages"),
            component: GridWithLargeRoundImages,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Our leadership team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Meet the experienced professionals who drive our company's success.",
                    translations.value.text
                )
            )
            .withArrayField(
                "teamMembers",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                        linkedInLink: { label: translations.value.linkedInLink, type: "text" },
                        xLink: { label: translations.value.xLink, type: "text" },
                    },
                    [
                        {
                            name: "Jessica Chen",
                            role: "Content Strategist",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/1`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Michael Rivera",
                            role: "UX Designer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/2`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Terrence Wilson",
                            role: "Creative Director",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Sophia Patel",
                            role: "Head of Product",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/2`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Olivia Martinez",
                            role: "Chief People Officer",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/3`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Aisha Johnson",
                            role: "Lead Engineer",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/4`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                    ],
                    {
                        name: "New team member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/5`,
                        linkedInLink: "",
                        xLink: "",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const gridWithRoundImagesBlock = computed<BlockDef>(() =>
        createBlock<GridWithRoundImagesProps>({
            typeId: "GridWithRoundImages",
            type: "team",
            name: t("page.blocks.team.gridWithRoundImages"),
            component: GridWithRoundImages,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Meet Our Experts", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our talented team brings together diverse skills and experiences to create innovative solutions that drive success for our clients and partners.",
                    translations.value.text
                )
            )
            .withArrayField(
                "teamMembers",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                    },
                    [
                        {
                            name: "Alex Johnson",
                            role: "Founder & CEO",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                        },
                        {
                            name: "Samuel Chen",
                            role: "Chief Technology Officer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/2`,
                        },
                        {
                            name: "Marcia Williams",
                            role: "Senior Developer",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/1`,
                        },
                        {
                            name: "Olivia Parker",
                            role: "UX/UI Designer",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/2`,
                        },
                        {
                            name: "James Rodriguez",
                            role: "Product Strategist",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                        },
                        {
                            name: "Sophia Kim",
                            role: "Content Strategist",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/3`,
                        },
                        {
                            name: "Daniel Lee",
                            role: "Creative Director",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/4`,
                        },
                        {
                            name: "Nathan Garcia",
                            role: "Lead Engineer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/5`,
                        },
                        {
                            name: "Isabella Martinez",
                            role: "Customer Experience Lead",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/4`,
                        },
                        {
                            name: "Ava Thompson",
                            role: "Head of Operations",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/5`,
                        },
                        {
                            name: "Zoe Richards",
                            role: "Full-Stack Developer",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/6`,
                        },
                        {
                            name: "Maya Patel",
                            role: "Marketing Specialist",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/7`,
                        },
                        {
                            name: "Emma Davis",
                            role: "Data Analyst",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/8`,
                        },
                        {
                            name: "Lily Wong",
                            role: "Brand Strategist",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/9`,
                        },
                        {
                            name: "Ethan Clark",
                            role: "Head of Development",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/6`,
                        },
                    ],
                    {
                        name: "New team member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/12`,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withImageAndShortParagraphBlock = computed<BlockDef>(() =>
        createBlock<WithImageAndShortParagraphProps>({
            typeId: "WithImageAndShortParagraph",
            type: "team",
            name: t("page.blocks.team.withImageAndShortParagraph"),
            component: WithImageAndShortParagraph,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Leadership Team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Meet the talented individuals who drive our company forward with their expertise, vision, and commitment to excellence in every project we undertake.",
                    translations.value.text
                )
            )
            .withArrayField(
                "teamMembers",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                        description: { label: translations.value.description, type: "text" },
                        linkedInLink: { label: translations.value.linkedInLink, type: "text" },
                        xLink: { label: translations.value.xLink, type: "text" },
                    },
                    [
                        {
                            name: "Michael Chen",
                            role: "Chief Technology Officer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                            description:
                                "Leading our technical initiatives with over 15 years of experience in software architecture and emerging technologies. His expertise in scalable solutions helps our clients stay ahead of industry trends and achieve their business goals.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Sarah Johnson",
                            role: "Head of Design",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/2`,
                            description:
                                "Brings creative vision to every project with her background in user experience and brand identity development. She combines aesthetic sensibility with practical functionality to create designs that resonate with target audiences and drive engagement.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "David Rodriguez",
                            role: "Product Director",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                            description:
                                "Transforms complex business requirements into elegant product solutions. With his expertise in agile methodologies, David ensures our development process remains efficient while delivering exceptional results.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Olivia Thompson",
                            role: "Strategic Operations Lead",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/4`,
                            description:
                                "Orchestrates our operational excellence with strategic insight and analytical precision. Her background in business transformation helps clients navigate complex challenges while optimizing processes for sustainable growth.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                    ],
                    {
                        name: "New team member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/5`,
                        description: "Add a brief description here",
                        linkedInLink: "",
                        xLink: "",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withLargeImagesBlock = computed<BlockDef>(() =>
        createBlock<WithLargeImagesProps>({
            typeId: "WithLargeImages",
            type: "team",
            name: t("page.blocks.team.withLargeImages"),
            component: WithLargeImages,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Our Team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Meet the talented people behind our success. Our team combines industry experience and innovative thinking to drive our company forward.",
                    translations.value.text
                )
            )
            .withArrayField(
                "teamMembers",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                        linkedInLink: { label: translations.value.linkedInLink, type: "text" },
                        xLink: { label: translations.value.xLink, type: "text" },
                    },
                    [
                        {
                            name: "Sarah Johnson",
                            role: "CEO & Founder",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/1`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Michael Chen",
                            role: "CTO",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Emily Rodriguez",
                            role: "Head of Design",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/2`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "David Thompson",
                            role: "Marketing Director",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/2`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Jessica Lee",
                            role: "Product Manager",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/3`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Robert Wilson",
                            role: "Lead Developer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                            linkedInLink: "#",
                            xLink: "#",
                        },
                    ],
                    {
                        name: "New team member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/5`,
                        linkedInLink: "",
                        xLink: "",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withVerticalImagesBlock = computed<BlockDef>(() =>
        createBlock<WithVerticalImagesProps>({
            typeId: "WithVerticalImages",
            type: "team",
            name: t("page.blocks.team.withVerticalImages"),
            component: WithVerticalImages,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Leadership Team", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Our experienced team of professionals brings diverse expertise and innovative thinking to every project, ensuring exceptional results that exceed expectations.",
                    translations.value.text
                )
            )
            .withArrayField(
                "teamMembers",
                arrayField(
                    translations.value.member,
                    "members",
                    {
                        name: { label: translations.value.name, type: "text" },
                        role: { label: translations.value.role, type: "text" },
                        imageSrc: { label: translations.value.src, type: "image" },
                        description: { label: translations.value.description, type: "text" },
                        linkedInLink: { label: translations.value.linkedInLink, type: "text" },
                        xLink: { label: translations.value.xLink, type: "text" },
                    },
                    [
                        {
                            name: "Michael Chen",
                            role: "Chief Technology Officer",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                            description:
                                "Leading our technical initiatives with over 15 years of experience in software architecture and emerging technologies. His expertise in scalable solutions helps our clients stay ahead of industry trends and achieve their business goals.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "Sarah Johnson",
                            role: "Head of Design",
                            imageSrc: `${AVATAR_API_BASE_URL}/f/2`,
                            description:
                                "Brings creative vision to every project with her background in user experience and brand identity development. Her combination of aesthetic sensibility with practical functionality creates designs that resonate with target audiences and drive engagement.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                        {
                            name: "David Rodriguez",
                            role: "Product Director",
                            imageSrc: `${AVATAR_API_BASE_URL}/m/3`,
                            description:
                                "Transforms complex business requirements into elegant product solutions. With his expertise in agile methodologies, David ensures our development process remains efficient while delivering exceptional results.",
                            linkedInLink: "#",
                            xLink: "#",
                        },
                    ],
                    {
                        name: "New team member",
                        role: "New role",
                        imageSrc: `${AVATAR_API_BASE_URL}/f/8`,
                        description: "Add a description here",
                        linkedInLink: "",
                        xLink: "",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            withSmallImagesBlock.value,
            fullWidthWithVerticalImagesBlock.value,
            gridWithLargeRoundImagesBlock.value,
            gridWithRoundImagesBlock.value,
            withImageAndShortParagraphBlock.value,
            withLargeImagesBlock.value,
            withVerticalImagesBlock.value,
        ]),
    };
}
